import type { SelectedFile } from "../types";

const SUPPORTED_EXTENSIONS = new Set([
  ".pdf",
  ".doc",
  ".docx",
  ".tif",
  ".tiff",
  ".jpg",
  ".jpeg",
  ".xlsx",
  ".csv",
]);

export interface RejectedSelection {
  file: SelectedFile;
  reason: string;
}

export interface FileSelectionValidationResult {
  accepted: SelectedFile[];
  rejected: RejectedSelection[];
}

// Lightweight client-side guard to catch likely oversized files before submit.
// Backend remains authoritative and may apply stricter/different limits.
const MAX_FILE_SIZE_BYTES = 40 * 1024 * 1024;

/**
 * Performs lightweight client-side pre-submit checks.
 *
 * Notes:
 * - Backend remains authoritative; this only improves UX.
 * - We intentionally only validate filename extension in the UI.
 */
export function splitSelectionByLikelyBackendPolicy(
  selectedFiles: SelectedFile[]
): FileSelectionValidationResult {
  const accepted: SelectedFile[] = [];
  const rejected: RejectedSelection[] = [];

  for (const file of selectedFiles) {
    const name = String(file?.name || "").trim();
    const extension = getExtension(name);

    if (!extension) {
      rejected.push({
        file,
        reason: "File has no extension.",
      });
      continue;
    }

    if (extension === ".xls") {
      rejected.push({
        file,
        reason: "Legacy .xls is not supported. Convert to .xlsx first.",
      });
      continue;
    }

    if (!SUPPORTED_EXTENSIONS.has(extension)) {
      rejected.push({
        file,
        reason: `Unsupported file type: ${extension}.`,
      });
      continue;
    }

    if (
      typeof file.size_bytes === "number" &&
      Number.isFinite(file.size_bytes) &&
      file.size_bytes > MAX_FILE_SIZE_BYTES
    ) {
      rejected.push({
        file,
        reason: `File is larger than 50 MB (${formatBytes(file.size_bytes)}).`,
      });
      continue;
    }

    accepted.push(file);
  }

  return { accepted, rejected };
}

function getExtension(filename: string): string {
  const trimmed = filename.trim();
  const lastDot = trimmed.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === trimmed.length - 1) {
    return "";
  }
  return trimmed.slice(lastDot).toLowerCase();
}

function formatBytes(value: number): string {
  const mb = value / (1024 * 1024);
  if (mb < 1) {
    return `${Math.max(1, Math.round(value / 1024))} KB`;
  }
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}
